import { DataGrid, GridToolbarContainer } from '@mui/x-data-grid';
import Typography from '@mui/material/Typography';
import { ProtectedRoutes } from "../../common/ProtectedRoute";
import MenuDrawer from "../Dashboard/components/MenuDrawer";
import React, { useEffect, useState } from "react";
import * as CONSTANTS from "../../utils/Constants";
import { useDispatch, useSelector } from "react-redux";
import { defaultTheme } from '../../styles/Theme';
import { Button, Box, Tooltip } from '@mui/material';
import { getUserAll } from '../../services/AuthService';
import CreateUserIcon from '@mui/icons-material/PersonAdd';
import { Link, Navigate, useNavigate} from "react-router-dom";
import DeleteUser from '../DeleteUser';
import { getUserRoles, isAdmin } from '../../store/reducers/role';
import { enqueueSnackbar } from 'notistack';
import { useDbData } from '../Dashboard/hooks/useDbData';

const styles = {
  headerStyle: {
    fontWeight: 'bold',
    marginTop: '2%',
    marginBottom: '2%',
    color: defaultTheme.palette.primary.main
  },
  dashboardStyle: {
    backgroundColor: defaultTheme.palette.primary.contrastText,
    marginBottom: '2%'
  },
  dataGridStyle: {
    boxShadow: 1,
    border: 1,
    borderColor: defaultTheme.palette.primary.contrastText,
    '& .MuiDataGrid-cell:hover': {
      color: defaultTheme.palette.primary.main,
    },
  }
}

export function UserManagement({allowedRoles}) {
  const dispatch = useDispatch();
  const _user = useSelector((state) => state.user)
  const [view, setView] = React.useState('Dashboard');
  const [drawerState, setDrawerState] = React.useState('closed');

  const countries = [
    { "value": "MY", "label": "Malaysia" },
    { "value": "PH", "label": "Philippines" },
    { "value": "VN", "label": "Vietnam" },
    { "value": "TH", "label": "Thailand" },
    { "value": "ID", "label": "Indonesia" },
    { "value": "SG", "label": "Singapore" },
    { "value": "AT", "label": "Austria" },
    { "value": "ES", "label": "Spain" },
    { "value": "CH", "label": "Switzerland" },
    { "value": "FR", "label": "France" },
    { "value": "DE", "label": "Germany" }
  ];

const isLoggedInUserAdmin = useSelector(isAdmin) ?? [];

  const countryCodeToName = (countryCode) => {
    if (countryCode === "admin") {
      return "All";
    }
    const country = countries.find((c) => c.value.toUpperCase() === countryCode.toUpperCase());
    return (country && country.label) ? country.label : "N/A";
  };

  const onViewSelected = (view) => {
    setView(view)
  }

  const onDrawerOpen = () => {
    setDrawerState('open')
  }

  const onDrawerClose = () => {
    setDrawerState('closed')
  }
  const [pageState, setPageState] = useState({
    isLoading: false,
    data: [],
    total: 0,
    page: 1,
    pageSize: 10,
    isFilterSelected: false
  })

  const [deletionState, setDeletionState] = useState({
    isDeleteModalOpen: false,
    isDeleted: false,
    userId: null,
    userEmail: null
  });

  const navigate = useNavigate(); 

  const displayText = (cellValue) => {
    return (
      <Tooltip title={cellValue}>
        <div>{cellValue}</div>
      </Tooltip>
    );
  };

  const displayActions = (cellValue, tooltipText) => {
    return (
      <Tooltip title={tooltipText}>
        <div>{cellValue}</div>
      </Tooltip>
    );
  };

  const displayCountryLabel = (cellValue) => {
    const countryNames = [];
    if(cellValue){
      let countryStr = cellValue + '';
      countryStr.split(",").forEach(
        str => {countryNames.push(countryCodeToName(str))}
      )
      cellValue = countryNames;
      const countryNamesStr = countryNames;
      cellValue = countryNamesStr.join(", ");
    }

    return (
      <Tooltip title={cellValue}>
        <div>{cellValue}</div>
      </Tooltip>
    );
  };

  const displayRoleName = (cellValue) => {
    return (
      <Tooltip title={cellValue}>
        <div>{cellValue}</div>
      </Tooltip>
    );
  };

  const handleAfterDelete = async (userId) => {
    setPageState(old => ({
      ...old, data: old.data.filter(user => {
        if (user.id !== userId) {
          console.log("deleted...removing user from page")
          return user;
        }
      }), total: old.data.length - 1
    }));
    setDeletionState({ ...deletionState, isDeleteModalOpen: false })
  }

  const handleClose = async () => {
    setDeletionState({ ...deletionState, isDeleteModalOpen: false })
  }

  const handleDelete = (params) => {
    setDeletionState({ ...deletionState, isDeleteModalOpen: true, userId: params.id, userEmail: params.email });
  }

  const columns = [
    {
      field: 'id', headerName: 'ID', width: 300,
      description: 'The identification used by the user management.',
      renderCell: (params) => displayText(params.value)
    },
    {
      field: 'c1', headerName: 'Country', width: 250,
      description: 'The country user is from.',
      renderCell: (params) => displayCountryLabel(params.value)
    },
    {
      field: 'c2', headerName: 'Username', width: 260,
      description: 'The username of the user',
      renderCell: (params) => displayText(params.value)
    },
    {
      field: 'c3', headerName: 'Role', width: 200,
      description: 'The role(s) assigned to a user',
      renderCell: (params) => displayRoleName(params.value)
    },
    {
      field: 'c4', headerName: 'Email ID', width: 260,
      description: 'The email of the user.',
      renderCell: (params) => displayText(params.value)
    },
    {
      field: 'edit', headerName: 'Edit', width: 100,
      description: 'The edit button to modify user information.',
      renderCell: (params) => {
          return displayActions(
            <Link to={"/user/edit/" + params.row.id}>
              <Button variant="outlined" color="info" size="small">Edit</Button>
            </Link>
         )
        }
    },
    {
      field: 'delete', headerName: 'Delete', width: 100,
      description: 'The delete button to remove user from LMS.',
      renderCell: (params) => {
          return displayActions(
            <Button variant="outlined" color="error" size="small" onClick={() => handleDelete(params.row)}>Delete</Button>
         )
        }
    }
  ];

  const getRoleName = (roles) => {
    let roleNames = ["admin", "logistics_manager", "logistics_agent"];
    
    let roleExists = [];
    if(roles){
      roles.forEach(role => {
        if(role.name && roleNames.includes(role.name)){
          roleExists.push(role.name);
        }
      });
    }

    if (roleExists.includes("admin")) {
      return "Admin";
    } else if (roleExists.includes("logistics_manager")) {
      return "Logistics Manager";
    } else if (roleExists.includes("logistics_agent")) {
      return "Logistics Agent";
    }

    return "Not Available";
  }

  const getCountries = (roles) => {
    let countries = [];
    let isUserAdmin = false;
    if(roles){
      roles.forEach(role => {
        if(role.name && role.name.startsWith("country_")){
          countries.push(role.name.split("_")[1]);
        }

        if(role.name === "admin") {
          isUserAdmin = true;
        }
      });
    }
    if(isUserAdmin){
      return ["admin"];
    }
    return countries;
  }
  
  const transformDbDataToGrid = (users) => {
    let rows = [];
    if(users.length > 0) {
      for (let i in users) {
        let temp = {
                id: users[i].id,
                c1: getCountries(users[i]?.roles),
                c2: users[i].username,
                c3: getRoleName(users[i]?.roles),
                c4: users[i].email,
              }
        rows.push(temp);
      }
    } else {
      rows.push({
        id: "",
        c1: "",
        c2: "",
        c3: "",
        c4: "",
      })
    }
    return rows;
  }
  
  const fetchData = async (pageStart, pageLimit) => {
    setPageState(old => ({ ...old, isLoading: true }))
    let page = (pageStart - 1) * pageLimit;
    let pageSize = pageLimit;
    const response = await getUserAll(_user)
    let users = response.data;
    const rows = transformDbDataToGrid(users);
    setPageState(old => ({ ...old, isLoading: false, data: rows, total: response.total }));
  }

  useEffect(() => {
    if (_user.loginState === CONSTANTS.LOGIN_STATE.yes) {
      fetchData(pageState.page, pageState.pageSize)
    }
       // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_user.loginState, pageState.page, pageState.pageSize])

  const userRoles = useSelector(getUserRoles) ?? [];
  const hasPermission = userRoles.some(role => allowedRoles.includes(role));
  if (!hasPermission) {
    enqueueSnackbar('Access Denied', { autoHideDuration: 2000, variant: 'error' });
    return <Navigate to="/dashboard" replace />;
  }

  const createUser = async () => {
    try {
      //CS-1866
      navigate('/user/create');
    } catch (ex) {
      console.error('HandleExport exception to : ', ex)
   
    }
};

  function CustomToolbar() {  
    const buttonBaseProps = {
      color: 'primary',
      size: 'small',
      startIcon: <CreateUserIcon />,
    };

    return (
      <GridToolbarContainer>
        <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        >
          <Button
            variant="outlined"
            {...buttonBaseProps}
            disabled={pageState.isLoading}
            onClick={createUser}
            sx={{ marginLeft: 'auto' }}
          >
            Create User
          </Button>
        </Box>
      </GridToolbarContainer>
    );
  }

  return <>
  <ProtectedRoutes>
  <MenuDrawer onDrawerOpen={onDrawerOpen} onDrawerClose={onDrawerClose} onViewSelected={onViewSelected} >
    <Typography variant="h5" style={styles.headerStyle}>User Management Dashboard</Typography>
    <div style={styles.dashboardStyle}>
   
      <DataGrid
        autoHeight
        initialState={{
          columns: {
            columnVisibilityModel: {
              id: false
            },
          },
        }}
        sx={styles.dataGridStyle}
        rows={pageState.data}
        rowCount={pageState.total}
        loading={pageState.isLoading}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        pagination
        page={pageState.page - 1}
        pageSize={pageState.pageSize}
        onPageChange={(newPage) => {
          setPageState(old => ({ ...old, page: newPage + 1 }))
        }}
        onPageSizeChange={(newPageSize) => setPageState(old => ({ ...old, pageSize: newPageSize }))}
        columns={columns}
        components={{ Toolbar: CustomToolbar }}
        componentsProps={{
          toolbar: {
            showQuickFilter: false
          }
        }}
      />
      <DeleteUser deletionState={deletionState} setDeletionState={setDeletionState} handleClose={handleClose} handleAfterDelete={handleAfterDelete}/>
    </div>
    </MenuDrawer>
    </ProtectedRoutes>
  </>;
}